import { useState } from 'react';
import { ArrowLeft, Edit, Trash2, Plus, History, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { PatientLayout } from './patient/PatientLayout';
import { LoadingState } from './common/LoadingState';
import { testsApi } from '../lib/api/tests';
import { useAuth } from '../lib/hooks/useAuth';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface SpirometryTestProps {
  onBack?: () => void;
  onNavigate?: (view: string) => void;
  onLogout?: () => void;
  selectedPatient?: any;
}

export function SpirometryTestWithCRUD({ onBack, onNavigate, onLogout, selectedPatient }: SpirometryTestProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const patientId = selectedPatient?.id || user?.patient_id || user?.id || 0;

  // Fetch spirometry history
  const { data: historyData, isLoading, refetch } = useQuery({
    queryKey: ['spirometry-history', patientId],
    queryFn: () => testsApi.getSpirometryHistory(patientId, { days: 90 }),
    enabled: !!patientId,
  });

  // View mode
  const [showForm, setShowForm] = useState(true);
  const [editingTest, setEditingTest] = useState<any>(null);
  const [deleteTestId, setDeleteTestId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form fields
  const [fev1Liters, setFev1Liters] = useState('');
  const [fev1Percent, setFev1Percent] = useState('');
  const [fvcLiters, setFvcLiters] = useState('');
  const [fvcPercent, setFvcPercent] = useState('');
  const [pef, setPef] = useState('');
  const [techniqueQuality, setTechniqueQuality] = useState<string>('good');
  const [testLocation, setTestLocation] = useState('home');
  const [notes, setNotes] = useState('');

  // Calculate FEV1/FVC ratio (as decimal, not percentage)
  const calculateRatio = () => {
    if (fev1Liters && fvcLiters) {
      const ratio = parseFloat(fev1Liters) / parseFloat(fvcLiters);
      return ratio.toFixed(4); // 4 decimal places for precision
    }
    return '';
  };
  
  // Display ratio as percentage for UI
  const displayRatio = () => {
    const ratio = calculateRatio();
    if (ratio) {
      return (parseFloat(ratio) * 100).toFixed(2) + '%';
    }
    return 'N/A';
  };

  const resetForm = () => {
    setFev1Liters('');
    setFev1Percent('');
    setFvcLiters('');
    setFvcPercent('');
    setPef('');
    setTechniqueQuality('good');
    setTestLocation('home');
    setNotes('');
    setEditingTest(null);
  };

  const handleEdit = (test: any) => {
    setEditingTest(test);
    setFev1Liters(test.fev1_liters?.toString() || '');
    setFev1Percent(test.fev1_percent_predicted?.toString() || '');
    setFvcLiters(test.fvc_liters?.toString() || '');
    setFvcPercent(test.fvc_percent_predicted?.toString() || '');
    setPef(test.pef_liters_per_min?.toString() || '');
    setTechniqueQuality(test.technique_quality || 'good');
    setTestLocation(test.test_location || 'home');
    setNotes(test.notes || '');
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!fev1Liters || !fev1Percent) {
      toast.error('Required fields missing', {
        description: 'Please enter FEV1 values',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const data = {
        fev1_liters: parseFloat(fev1Liters),
        fev1_percent_predicted: parseFloat(fev1Percent),
        fvc_liters: fvcLiters ? parseFloat(fvcLiters) : undefined,
        fvc_percent_predicted: fvcPercent ? parseFloat(fvcPercent) : undefined,
        fev1_fvc_ratio: calculateRatio() ? parseFloat(calculateRatio()) : undefined,
        pef_liters_per_min: pef ? parseFloat(pef) : undefined,
        quality_grade: techniqueQuality,  // ✅ Fixed: was technique_quality
        test_location: testLocation,
        technician_notes: notes || undefined,  // ✅ Fixed: was notes
      };

      if (editingTest) {
        // Update existing
        await testsApi.updateSpirometry(editingTest.id, data);
        toast.success('Spirometry test updated successfully!');
      } else {
        // Create new - ✅ Fixed: Send patient_id directly
        await testsApi.recordSpirometry({
          patient_id: patientId,
          ...data,
        });
        toast.success('Spirometry test recorded successfully!');
      }

      resetForm();
      refetch();
      queryClient.invalidateQueries({ queryKey: ['patient-dashboard'] });
    } catch (error: any) {
      toast.error('Failed to save test', {
        description: error?.message || 'An error occurred',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTestId) return;

    try {
      await testsApi.deleteSpirometry(deleteTestId);
      toast.success('Spirometry test deleted successfully!');
      setDeleteTestId(null);
      refetch();
      queryClient.invalidateQueries({ queryKey: ['patient-dashboard'] });
    } catch (error: any) {
      toast.error('Failed to delete test', {
        description: error?.message || 'An error occurred',
      });
    }
  };

  const getFEV1Status = (percent: number) => {
    if (percent >= 80) return { label: 'Normal', color: 'bg-[#14CAF4]' };
    if (percent >= 50) return { label: 'Moderate', color: 'bg-accent' };
    return { label: 'Severe', color: 'bg-destructive' };
  };

  return (
    <PatientLayout currentView="tests" onNavigate={onNavigate} onLogout={onLogout}>
      <div className="p-4 space-y-4">
        {/* Header with toggle */}
        <div className="flex items-center justify-between">
          {onBack && (
            <Button variant="outline" onClick={onBack} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          )}
          <Button 
            variant="outline" 
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2"
          >
            {showForm ? <History className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? 'View History' : 'Record New'}
          </Button>
        </div>

        {showForm ? (
          /* Input Form */
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-primary flex items-center gap-2">
                <Activity className="w-5 h-5" />
                {editingTest ? 'Edit Spirometry Test' : 'Record Spirometry Test'}
              </CardTitle>
              <p className="text-muted-foreground">Enter test results</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* FEV1 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fev1-liters">FEV1 (Liters) *</Label>
                  <Input
                    id="fev1-liters"
                    type="number"
                    step="0.01"
                    placeholder="e.g., 2.45"
                    value={fev1Liters}
                    onChange={(e) => setFev1Liters(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fev1-percent">FEV1 % Predicted *</Label>
                  <Input
                    id="fev1-percent"
                    type="number"
                    step="0.1"
                    placeholder="e.g., 65.5"
                    value={fev1Percent}
                    onChange={(e) => setFev1Percent(e.target.value)}
                  />
                  {fev1Percent && (
                    <Badge className={getFEV1Status(parseFloat(fev1Percent)).color}>
                      {getFEV1Status(parseFloat(fev1Percent)).label}
                    </Badge>
                  )}
                </div>
              </div>

              {/* FVC */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fvc-liters">FVC (Liters)</Label>
                  <Input
                    id="fvc-liters"
                    type="number"
                    step="0.01"
                    placeholder="e.g., 3.20"
                    value={fvcLiters}
                    onChange={(e) => setFvcLiters(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fvc-percent">FVC % Predicted</Label>
                  <Input
                    id="fvc-percent"
                    type="number"
                    step="0.1"
                    placeholder="e.g., 72.0"
                    value={fvcPercent}
                    onChange={(e) => setFvcPercent(e.target.value)}
                  />
                </div>
              </div>

              {/* Ratio */}
              {calculateRatio() && (
                <div className="p-3 bg-secondary rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">FEV1/FVC Ratio:</span>
                    <Badge variant="outline">{displayRatio()}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {parseFloat(calculateRatio()) < 0.70 ? 'May indicate airflow obstruction' : 'Normal ratio'}
                  </p>
                </div>
              )}

              {/* PEF */}
              <div className="space-y-2">
                <Label htmlFor="pef">Peak Expiratory Flow (L/min)</Label>
                <Input
                  id="pef"
                  type="number"
                  step="1"
                  placeholder="e.g., 350"
                  value={pef}
                  onChange={(e) => setPef(e.target.value)}
                />
              </div>

              {/* Technique Quality */}
              <div className="space-y-2">
                <Label htmlFor="technique">Technique Quality</Label>
                <Select value={techniqueQuality} onValueChange={setTechniqueQuality}>
                  <SelectTrigger id="technique">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excellent">Excellent</SelectItem>
                    <SelectItem value="good">Good</SelectItem>
                    <SelectItem value="fair">Fair</SelectItem>
                    <SelectItem value="poor">Poor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Test Location */}
              <div className="space-y-2">
                <Label htmlFor="location">Test Location</Label>
                <Select value={testLocation} onValueChange={setTestLocation}>
                  <SelectTrigger id="location">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="home">Home</SelectItem>
                    <SelectItem value="clinic">Clinic</SelectItem>
                    <SelectItem value="hospital">Hospital</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Any additional notes about the test..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                {editingTest && (
                  <Button variant="outline" onClick={resetForm} className="flex-1">
                    Cancel
                  </Button>
                )}
                <Button 
                  onClick={handleSubmit} 
                  disabled={isSubmitting}
                  className="flex-1 bg-primary"
                >
                  {isSubmitting ? 'Saving...' : editingTest ? 'Update Test' : 'Save Test'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* History View */
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-primary flex items-center gap-2">
                <History className="w-5 h-5" />
                Spirometry Test History
              </CardTitle>
              <p className="text-muted-foreground">Past 90 days</p>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <LoadingState message="Loading test history..." />
              ) : !historyData?.results || historyData.results.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No tests recorded yet</p>
                  <Button onClick={() => setShowForm(true)} className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Record First Test
                  </Button>
                </div>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {historyData.results.map((test: any) => (
                    <div
                      key={test.id}
                      className="p-4 border-2 border-border rounded-xl hover:bg-secondary/50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-medium">{new Date(test.test_date).toLocaleDateString()}</p>
                          <Badge className={getFEV1Status(test.fev1_percent_predicted || 0).color}>
                            FEV1: {test.fev1_percent_predicted}% predicted
                          </Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleEdit(test)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeleteTestId(test.id)}
                            className="text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-muted-foreground">FEV1:</span>
                          <span className="ml-2 font-medium">{test.fev1_liters}L ({test.fev1_percent_predicted}%)</span>
                        </div>
                        {test.fvc_liters && (
                          <div>
                            <span className="text-muted-foreground">FVC:</span>
                            <span className="ml-2 font-medium">{test.fvc_liters}L ({test.fvc_percent_predicted}%)</span>
                          </div>
                        )}
                        {test.fev1_fvc_ratio && (
                          <div>
                            <span className="text-muted-foreground">Ratio:</span>
                            <span className="ml-2 font-medium">{test.fev1_fvc_ratio}%</span>
                          </div>
                        )}
                        {test.pef_liters_per_min && (
                          <div>
                            <span className="text-muted-foreground">PEF:</span>
                            <span className="ml-2 font-medium">{test.pef_liters_per_min} L/min</span>
                          </div>
                        )}
                        <div>
                          <span className="text-muted-foreground">Technique:</span>
                          <span className="ml-2 font-medium">{test.technique_quality || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Location:</span>
                          <span className="ml-2 font-medium">{test.test_location || 'N/A'}</span>
                        </div>
                      </div>
                      {test.notes && (
                        <p className="text-sm text-muted-foreground mt-2 italic">
                          Note: {test.notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteTestId} onOpenChange={() => setDeleteTestId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Spirometry Test?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete this test result from your history.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </PatientLayout>
  );
}